import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useReviewConfigs } from '@/hooks/useReviewConfigs';

import { VariableAutocomplete } from '../VariableAutocomplete';
import { VariablePicker } from '../VariablePicker';

import type {
  AnalyticsNodeMeta,
  AgentNodeMeta,
  ClassifierNodeMeta,
  QualityNodeMeta,
  ReviewNodeMeta,
} from '../VariablePicker';
import type { RunReviewNodeData } from '@repo/shared-types';

interface RunReviewConfigProps {
  data: RunReviewNodeData;
  onChange: (data: RunReviewNodeData) => void;
  responseFieldsSchema?: Record<string, unknown>;
  reviewNodeMetas?: ReviewNodeMeta[];
  classifierNodeMetas?: ClassifierNodeMeta[];
  qualityNodeMetas?: QualityNodeMeta[];
  agentNodeMetas?: AgentNodeMeta[];
  analyticsNodeMetas?: AnalyticsNodeMeta[];
  triggerType?: 'call' | 'dealerkit' | 'any' | 'scheduled' | 'webhook' | 'event';
}

export const RunReviewConfig = ({
  data,
  onChange,
  responseFieldsSchema,
  reviewNodeMetas,
  classifierNodeMetas,
  qualityNodeMetas,
  agentNodeMetas,
  analyticsNodeMetas,
  triggerType,
}: RunReviewConfigProps) => {
  const { data: reviewConfigs } = useReviewConfigs();
  const update = (patch: Partial<RunReviewNodeData>) => onChange({ ...data, ...patch });
  const mappings = data.inputMappings ?? [];

  const handleAddMapping = () => {
    update({ inputMappings: [...mappings, { contextKey: '', workflowVar: '' }] });
  };

  const handleRemoveMapping = (idx: number) => {
    update({ inputMappings: mappings.filter((_, i) => i !== idx) });
  };

  const handleUpdateMapping = (
    idx: number,
    patch: Partial<{ contextKey: string; workflowVar: string }>,
  ) => {
    const updated = mappings.map((m, i) => (i === idx ? { ...m, ...patch } : m));
    update({ inputMappings: updated });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Label</label>
        <Input
          value={data.label}
          onChange={(e) => update({ label: e.target.value })}
          placeholder="Node label"
        />
      </div>

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Review Config</label>
        <select
          value={data.reviewConfigId}
          onChange={(e) => update({ reviewConfigId: e.target.value })}
          className="border-input bg-background ring-offset-background focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
        >
          <option value="">Select a review config...</option>
          {(reviewConfigs ?? []).map((rc) => (
            <option key={rc.id} value={rc.id}>
              {rc.name}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-1">
        <label className="text-muted-foreground text-xs font-medium">Output Key</label>
        <Input
          value={data.outputKey}
          onChange={(e) => update({ outputKey: e.target.value })}
          placeholder="review"
        />
      </div>

      <label className="flex items-start gap-2 text-xs">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={data.sessionOnly ?? false}
          onChange={(e) => update({ sessionOnly: e.target.checked })}
        />
        <span>
          This session only
          <span className="text-muted-foreground block text-[10px]">
            WhatsApp, web chat and email only. Off, the review also sees the earlier messages in the
            thread. On, it sees this session and nothing before it. Voice calls are unaffected.
          </span>
        </span>
      </label>

      <div className="space-y-2">
        <label className="text-muted-foreground text-xs font-medium">Additional Input</label>
        <p className="text-muted-foreground text-[10px]">
          Pass data from upstream nodes into this review. The AI will see these as additional
          context alongside the call transcript.
        </p>
        {mappings.map((mapping, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <Input
              value={mapping.contextKey}
              onChange={(e) => handleUpdateMapping(idx, { contextKey: e.target.value })}
              placeholder="context label"
              className="w-1/3"
            />
            <span className="text-muted-foreground text-xs">&rarr;</span>
            <VariableAutocomplete
              value={mapping.workflowVar}
              onChange={(v) => handleUpdateMapping(idx, { workflowVar: v })}
              placeholder="workflow variable..."
              responseFieldsSchema={responseFieldsSchema}
              reviewNodeMetas={reviewNodeMetas}
              classifierNodeMetas={classifierNodeMetas}
              qualityNodeMetas={qualityNodeMetas}
              agentNodeMetas={agentNodeMetas}
              analyticsNodeMetas={analyticsNodeMetas}
              className="flex-1"
            />
            <VariablePicker
              onSelect={(v) => handleUpdateMapping(idx, { workflowVar: mapping.workflowVar + v })}
              responseFieldsSchema={responseFieldsSchema}
              reviewNodeMetas={reviewNodeMetas}
              classifierNodeMetas={classifierNodeMetas}
              qualityNodeMetas={qualityNodeMetas}
              agentNodeMetas={agentNodeMetas}
              analyticsNodeMetas={analyticsNodeMetas}
              triggerType={triggerType}
            />
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => handleRemoveMapping(idx)}
            >
              <Trash2 size={12} />
            </Button>
          </div>
        ))}
        <Button variant="outline" size="sm" className="w-full" onClick={handleAddMapping}>
          <Plus size={14} className="mr-1" />
          Add Input
        </Button>
      </div>
    </div>
  );
};
